import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import Bugsnag from "@/lib/bugsnag";
import { SwmsDocument } from "@/types/swms";
import type { ExportFormat } from "@/components/FormatSelector";
import { StandardsSearchResult, type MatchedStandard } from "@/types/standards";
import { useToast } from "@/hooks/use-toast";
import { validateEmail } from "@/lib/email-validation";
import { validatePhone } from "@/lib/phone-validation";
import { PhoneInput } from "@/components/ui/phone-input";
import { useLocationDetection } from "@/hooks/use-location-detection";
import { useGeneratorConfig } from "@/hooks/use-generator-config";
import { useDomainGenerator } from "@/hooks/use-domain-generator";
import { useEdgeFunctions } from "@/hooks/use-edge-functions";
import { useVariantAssignment } from "@/hooks/use-variant-assignment";
import { useDocumentHead } from "@/hooks/use-document-head";
import { useTabProcessingState } from "@/hooks/use-tab-processing-state";
import { DEFAULT_GENERATOR_CONFIG, DEFAULT_UI_LABELS, type GeneratorUILabels } from "@/types/generator";
import { shortRunFromRunId, formatAttachmentKind, formatCorrelation } from "@/lib/attachment-context";
import { COUNTRIES } from "@/lib/countries";
import type { Country } from "react-phone-number-input";
import {
  trackPageVisited,
  trackButtonClicked,
  trackEmailCaptureFormModal,
  trackFileSuccessfullyDownloaded,
  trackLeadCreated,
  trackDownloadCompleteModal,
  trackUserSubmittedRating,
  trackUserSubmittedReferralSource,
  trackFeedbackCommented,
  setGeneratorIdentity,
  resolveDocumentType,
  EventContext } from
"@/lib/analytics";

import { ChatInterface } from "@/components/ChatInterface";
import { ProcessingCarousels, CarouselSubItem } from "@/components/ProcessingCarousels";
import { ProcessingStepList, ProcessingStep } from "@/components/ProcessingStepList";
import { ProcessingSkeleton } from "@/components/ProcessingSkeleton";
import { AttachmentAnalysisRows, type AttachmentKindBlock, type AttachmentFileRow } from "@/components/AttachmentAnalysisRows";
import { CorrelationSummaryRow, type CorrelationSummaryRowProps, type HazardProvenanceItem } from "@/components/CorrelationSummaryRow";
import { DomainNotConfigured } from "@/components/DomainNotConfigured";
import { DevModeChecklist } from "@/components/DevModeChecklist";
import { AboutModal } from "@/components/AboutModal";
import { SuccessMarketingBanner } from "@/components/SuccessMarketingBanner";
import SitemateFooter from "@/components/SitemateFooter";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle } from
"@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Download, Loader2, CheckCircle2, RefreshCw, Link2, Star } from "lucide-react";
import { getVisitorId } from "@/lib/visitor-id";
type ModalState = "closed" | "processing" | "ready" | "success";

// Domain-specific UI labels for multi-domain generators (e.g., Inspection)
const DOMAIN_UI_LABELS: Record<string, Partial<GeneratorUILabels>> = {
  safety: {
    analysis_heading: 'Hazard Analysis',
    scanning_label: 'Identifying Hazard Keywords',
    scanning_message: 'Scanning prompt for hazard keywords...',
    identified_label: 'Hazards',
    none_detected_message: 'No specific hazards detected',
    step_detecting: 'Detecting hazards',
    step_processing: 'Processing controls',
  },
  quality: {
    analysis_heading: 'Requirements Analysis',
    scanning_label: 'Identifying Quality Requirements',
    scanning_message: 'Scanning prompt for quality requirements...',
    identified_label: 'Requirements',
    none_detected_message: 'No specific requirements detected',
    step_detecting: 'Detecting requirements',
    step_processing: 'Processing inspections',
  },
  environmental: {
    analysis_heading: 'Environmental Analysis',
    scanning_label: 'Identifying Environmental Aspects',
    scanning_message: 'Scanning prompt for environmental aspects...',
    identified_label: 'Aspects',
    none_detected_message: 'No specific aspects detected',
    step_detecting: 'Detecting aspects',
    step_processing: 'Processing mitigations',
  },
};
const REFERRAL_OPTIONS = [
{
  value: "google",
  label: "Google"
},
{
  value: "linkedin",
  label: "LinkedIn"
},
{
  value: "friend",
  label: "A Friend"
},
{
  value: "colleague",
  label: "A Colleague"
}];


// Feedback data structure for future Mixpanel integration
interface FeedbackData {
  swmsSummary: string;
  email: string;
  starRating: number;
  referralSource: string;
  comments: string;
}

// Helper to format domain with proper capitalization for display
function formatDomainForDisplay(
  primaryDomain: string | undefined,
  displayName: string | undefined,
  acronym: string | undefined
): string {
  if (!primaryDomain) {
    // Fallback: use acronym/display_name to construct domain
    const name = (acronym || displayName || 'Generator').replace(/\s+/g, '');
    return `www.${name}Generator.ai`;
  }

  const clean = primaryDomain.replace(/^www\./i, '');
  const genIdx = clean.toLowerCase().indexOf('generator');
  
  if (genIdx === -1) {
    // No "generator" in domain - just capitalize first letter
    return `www.${clean.charAt(0).toUpperCase() + clean.slice(1)}`;
  }

  const prefix = clean.substring(0, genIdx);
  const suffix = clean.substring(genIdx + 'generator'.length); // typically ".ai"
  
  // Try to match prefix with acronym (case-insensitive, no spaces)
  const acronymNormalized = (acronym || '').toLowerCase().replace(/\s+/g, '');
  if (prefix.toLowerCase() === acronymNormalized) {
    return `www.${(acronym || prefix).replace(/\s+/g, '')}Generator${suffix}`;
  }
  
  // Try to match against display_name words (minus "Generator")
  if (displayName) {
    const words = displayName.replace(/\s*Generator\s*/gi, '').split(/\s+/);
    const capitalizedPrefix = words.join('');
    if (capitalizedPrefix.toLowerCase() === prefix.toLowerCase()) {
      return `www.${capitalizedPrefix}Generator${suffix}`;
    }
  }
  
  // Fallback: capitalize first letter of prefix
  return `www.${prefix.charAt(0).toUpperCase() + prefix.slice(1)}Generator${suffix}`;
}

// Helper to check if an error is a rate limit response
function isRateLimitError(error: unknown): {isRateLimit: boolean;message?: string;} {
  if (error && typeof error === "object") {
    const err = error as {message?: string;context?: {status?: number;body?: string;};};

    // Check for 429 status in error context
    if (err.context?.status === 429) {
      try {
        const body = JSON.parse(err.context.body || "{}");
        return { isRateLimit: true, message: body.error };
      } catch {
        return { isRateLimit: true, message: "You've made too many requests. Please try again later." };
      }
    }

    // Check for rate limit message in error
    if (err.message?.includes("429") || err.message?.includes("Too Many Requests")) {
      return { isRateLimit: true, message: "You've made too many requests. Please try again later." };
    }
  }
  return { isRateLimit: false };
}
const Index = () => {
  // Detect generator based on current domain
  const {
    slug: generatorSlug,
    displayName: generatorDisplayName,
    isLoading: isDomainLoading,
    isUnconfiguredDomain,
    isDevMode,
    detectedHostname,
    isOverridden
  } = useDomainGenerator();
  const { config: generatorConfig, isLoading: isConfigLoading } = useGeneratorConfig(generatorSlug, { allowInactive: isOverridden });

  // A/B variant assignment
  const {
    variantKey,
    layoutName,
    variantConfig,
    variants: availableVariants,
    isLoading: isVariantLoading,
    setVariantKey
  } = useVariantAssignment(generatorSlug, isDevMode);

  // Use fetched config or fallback to default
  const config = generatorConfig || DEFAULT_GENERATOR_CONFIG;

  // Create edge function routing config from generator config
  const edgeFunctions = useEdgeFunctions({
    slug: config.slug,
    use_generic_pipeline: config.use_generic_pipeline ?? config.slug !== 'swms',
    supported_export_formats: config.supported_export_formats || ['docx'],
    standards_behavior: config.standards_behavior
  });
  const isInitializing = isDomainLoading || isConfigLoading || isVariantLoading;

  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([]);
  const [carouselSubItems, setCarouselSubItems] = useState<CarouselSubItem[]>([]);
  const [matchedStandards, setMatchedStandards] = useState<MatchedStandard[]>([]);
  const [detectedCategories, setDetectedCategories] = useState<string[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchComplete, setIsSearchComplete] = useState(false);
  const [totalSourcesCount, setTotalSourcesCount] = useState(0);
  const [modalState, setModalState] = useState<ModalState>("closed");
  const [isValidating, setIsValidating] = useState(false);
  const [showExamplePrompts, setShowExamplePrompts] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<SwmsDocument | null>(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  
  const [isExporting, setIsExporting] = useState(false);
  const [lastSubmittedCountry, setLastSubmittedCountry] = useState("");
  const [lastSubmittedState, setLastSubmittedState] = useState("");
  const [lastSubmittedActivity, setLastSubmittedActivity] = useState("");
  const [selectedExportFormat, setSelectedExportFormat] = useState<ExportFormat>("docx");

  // Detect user location for phone country default (GLOBAL mode = no market filtering)
  const { countryCode: detectedCountryCode, isLoading: isPhoneLocationLoading } = useLocationDetection(["GLOBAL"]);
  const defaultPhoneCountry: Country = isPhoneLocationLoading ?
  "AU" // Temporary default while detecting
  : detectedCountryCode as Country || "AU"; // Fallback if detection fails

  // Dev mode: auto-fill email and phone to skip manual entry during QA.
  // Re-runs when the modal opens to handle resets, and waits for location detection.
  useEffect(() => {
    if (!isDevMode || isPhoneLocationLoading) return;
    if (modalState !== "ready") return; // Only fill when email capture shows
    const devPhoneMap: Record<string, string> = {
      AU: "+61400000000",
      US: "+14155550100",
      NZ: "+64210000000",
      GB: "+447700900000",
      IE: "+353850000000",
      CA: "+16135550100",
      PH: "+639171234567"
    };
    setEmail("dev@sitemate.com");
    setPhone(devPhoneMap[detectedCountryCode] || devPhoneMap["AU"]);
    setFirstName("Dev");
    setLastName("User");
  }, [isDevMode, isPhoneLocationLoading, detectedCountryCode, modalState]);

  // Feedback state - grouped for clean Mixpanel integration
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({
    swmsSummary: "",
    email: "",
    starRating: 0,
    referralSource: "",
    comments: ""
  });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const hasTrackedFeedbackComment = useRef(false);
  const [chatResetKey, setChatResetKey] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isBannerCollapsed, setIsBannerCollapsed] = useState(false);
  const [isModalInitializing, setIsModalInitializing] = useState(false);
  const [detectedDomain, setDetectedDomain] = useState<string | null>(null);
  const [dynamicUiLabels, setDynamicUiLabels] = useState<GeneratorUILabels | null>(null);
  const [attachmentBlocks, setAttachmentBlocks] = useState<AttachmentKindBlock[]>([]);
  const [correlationStatus, setCorrelationStatus] = useState<CorrelationSummaryRowProps>({ status: "idle" });
  const abortControllerRef = useRef<AbortController | null>(null);
  const generationStartTimeRef = useRef<number>(0);
  const lastUsedModelRef = useRef<string>("");
  const generationLogIdRef = useRef<string | null>(null);
  const { toast } = useToast();

  // Helper to update generation log row via SECURITY DEFINER RPC.
  // Direct .update() silently no-ops for anon users (no SELECT policy = row invisible to PostgREST).
  const updateGenerationLog = useCallback(async (id: string | null, updates: Record<string, unknown>) => {
    if (!id) return;
    try {
      const { error } = await supabase.rpc('update_generation_log', {
        p_id: id,
        p_status: updates.status as string,
        p_completed_at: (updates.completed_at as string) ?? null,
        p_duration_ms: (updates.duration_ms as number) ?? null,
        p_error_message: (updates.error_message as string) ?? null,
        p_error_stage: (updates.error_stage as string) ?? null,
      });
      if (error) console.warn('Failed to update generation log:', error.message);
    } catch (e) {
      // Logging failures must never block generation
      console.warn('Failed to update generation log:', e);
    }
  }, []);
  const hasTrackedPageVisit = useRef(false);
  const funnelSessionId = useRef(crypto.randomUUID());

  // Fire-and-forget funnel event logger (no PII, write-only)
  const logFunnelEvent = useCallback((eventType: string, metadata?: Record<string, unknown>) => {
    supabase.from('page_funnel_events').insert({
      session_id: funnelSessionId.current,
      visitor_id: getVisitorId(),
      generator_slug: config.slug,
      event_type: eventType,
      variant_key: variantKey,
      metadata: metadata ?? {}
    } as never).then(); // fire-and-forget
  }, [config.slug, variantKey]);

  // Dynamic SEO head tags based on generator config.
  // IMPORTANT: use the *loaded* config only (or null) to avoid flashing SWMS title.
  useDocumentHead(generatorConfig);
  useTabProcessingState(modalState, generatorConfig);

  // Cache generator slug for analytics once hook resolves
  useEffect(() => {
    if (!isDomainLoading && generatorSlug) {
      setGeneratorIdentity(generatorSlug, generatorDisplayName);
    }
  }, [generatorSlug, generatorDisplayName, isDomainLoading]);

  // Track page visit once (wait for generator slug to be cached first)
  useEffect(() => {
    if (!hasTrackedPageVisit.current && !isDomainLoading && !isConfigLoading && generatorSlug && generatorConfig) {
      trackPageVisited();
      logFunnelEvent('page_visit');
      hasTrackedPageVisit.current = true;
    }
  }, [generatorSlug, isDomainLoading, isConfigLoading, generatorConfig, logFunnelEvent]);

  const getTrackedDocumentType = useCallback((document?: Pick<SwmsDocument, "TYPE" | "ACTIVITY"> | null, submittedActivity?: string) => {
    return resolveDocumentType({
      generatorSlug: config.slug,
      rawType: document?.TYPE,
      generatedActivity: document?.ACTIVITY,
      submittedActivity: submittedActivity ?? lastSubmittedActivity,
    });
  }, [config.slug, lastSubmittedActivity]);

  // Get generator-specific processing step labels from database-driven config
  const getProcessingStepLabels = useCallback(() => {
    const acronym = config.acronym || 'Document';
    const documentName = (config.display_name || acronym).replace(/\s*Generator$/i, '');
    const labels = config.ui_labels || DEFAULT_UI_LABELS;

    return {
      detecting: labels.step_detecting || DEFAULT_UI_LABELS.step_detecting,
      hazards: labels.step_processing || DEFAULT_UI_LABELS.step_processing,
      searching: labels.step_searching || DEFAULT_UI_LABELS.step_searching,
      completing: `Completing ${documentName}`
    };
  }, [config.acronym, config.display_name, config.ui_labels]);

  // Determine if this generator requires standards search
  const shouldSearchStandards = config.standards_behavior?.requires_standards_check !== false;
  const shouldAnalyzeActivity = config.standards_behavior?.requires_activity_analysis !== false;

  const initializeProcessingSteps = useCallback((attachmentCounts?: { photo: number; plan: number; doc: number }) => {
    const labels = getProcessingStepLabels();
    const steps: ProcessingStep[] = [
    // Review phase - validation always happens
    {
      id: "validating",
      label: "Validating request",
      status: "active",
      phase: "review"
    }];


    // Only add activity analysis and detection steps if required (SWMS-style hazard detection)
    if (shouldAnalyzeActivity) {
      steps.push(
        {
          id: "analyzing",
          label: "Reviewing activity",
          status: "pending",
          phase: "review"
        },
        {
          id: "detecting",
          label: labels.detecting,
          status: "pending",
          phase: "review"
        }
      );
    }

    // Attachment analysis steps (only when files of that kind are present)
    if (attachmentCounts) {
      if (attachmentCounts.photo > 0) {
        steps.push({ id: "analyzing-photos", label: "Analyzing photos", status: "pending", phase: "review" });
      }
      if (attachmentCounts.plan > 0) {
        steps.push({ id: "analyzing-drawings", label: "Analyzing drawings", status: "pending", phase: "review" });
      }
      if (attachmentCounts.doc > 0) {
        steps.push({ id: "analyzing-docs", label: "Analyzing documents", status: "pending", phase: "review" });
      }
      const hasAnyAttachment = attachmentCounts.photo + attachmentCounts.plan + attachmentCounts.doc > 0;
      if (hasAnyAttachment) {
        steps.push({ id: "cross-referencing", label: "Cross-referencing insights", status: "pending", phase: "review" });
      }
    }

    // Only add standards search step if required for this generator
    if (shouldSearchStandards) {
      steps.push({
        id: "searching",
        label: labels.searching,
        status: "pending",
        phase: "review"
      });
    }

    // Generation phase
    steps.push(
      {
        id: "structuring",
        label: "Building structure",
        status: "pending",
        phase: "generate"
      },
      {
        id: "hazards",
        label: labels.hazards,
        status: "pending",
        phase: "generate"
      },
      {
        id: "completing",
        label: labels.completing,
        status: "pending",
        phase: "generate"
      }
    );

    setProcessingSteps(steps);
    setCarouselSubItems([]);
    setMatchedStandards([]);
    setDetectedCategories([]);
    setShowCategories(false);
    setIsSearching(false);
    setIsSearchComplete(false);
    setDetectedDomain(null);
    setDynamicUiLabels(null);
    setAttachmentBlocks([]);
    setCorrelationStatus({ status: "idle" });
  }, [getProcessingStepLabels, shouldSearchStandards, shouldAnalyzeActivity]);
  const updateStepStatus = (stepId: string, status: ProcessingStep["status"]) => {
    setProcessingSteps((prev) =>
    prev.map((step) =>
    step.id === stepId ?
    {
      ...step,
      status
    } :
    step
    )
    );
  };

  // Helper to ensure minimum display time for each step
  const waitMinimum = async (startTime: number, minimumMs: number = 900) => {
    const elapsed = Date.now() - startTime;
    if (elapsed < minimumMs) {
      await new Promise((resolve) => setTimeout(resolve, minimumMs - elapsed));
    }
  };

  // Submit feedback - ready for Mixpanel integration
  const submitFeedback = useCallback(() => {
    if (feedbackSubmitted) return;

    // Only mark as submitted if user has interacted with feedback (rated at least)
    // Analytics tracking is handled separately via individual tracking functions
    if (feedbackData.starRating > 0) {
      setFeedbackSubmitted(true);
    }
  }, [feedbackData.starRating, feedbackSubmitted]);
  const handleGenerate = async (data: {
    activity: string;
    country: string;
    state: string;
    model: string;
    photos: string[];
    plans: string[];
    docs?: string[];
    photoNames?: string[];
    planNames?: string[];
    docNames?: string[];
    exportFormat: ExportFormat;
  }) => {
    // Normalize attachment arrays
    const photos = data.photos ?? [];
    const plans = data.plans ?? [];
    const docs = data.docs ?? [];
    const photoNames = data.photoNames ?? photos.map((_, i) => `photo-${i + 1}`);
    const planNames = data.planNames ?? plans.map((_, i) => `drawing-${i + 1}.pdf`);
    const docNames = data.docNames ?? docs.map((_, i) => `document-${i + 1}`);
    const attachmentCounts = { photo: photos.length, plan: plans.length, doc: docs.length };
    const hasAnyAttachment = attachmentCounts.photo + attachmentCounts.plan + attachmentCounts.doc > 0;
    // Stable run id used to tag every insight (analyze-activity, analyze-attachments, correlations)
    const runId = crypto.randomUUID();

    // Store the selected export format for use in handleDownload
    setSelectedExportFormat(data.exportFormat);

    // Cancel any existing generation
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Capture generation start time and model for analytics
    generationStartTimeRef.current = Date.now();
    lastUsedModelRef.current = data.model;

    // Create generation log row (fire-and-forget insert, store ID for updates)
    // Generate UUID client-side to avoid needing SELECT permission on the table
    const logId = crypto.randomUUID();
    generationLogIdRef.current = logId;
    try {
      await supabase.from('generation_logs').insert({
        id: logId,
        generator_slug: config.slug,
        visitor_id: getVisitorId(),
        activity: data.activity.substring(0, 500),
        country: data.country || null,
        state_province: data.state || null,
        model: data.model || null,
        export_format: data.exportFormat || null,
        status: 'started'
      });
    } catch (e) {
      console.warn('Failed to create generation log:', e);
    }

    logFunnelEvent('prompt_submitted', { country: data.country, model: data.model });
    setIsValidating(true);
    // Update log to validating
    updateGenerationLog(generationLogIdRef.current, { status: 'validating' });

    // Phase 2 optimization: fire validate + analyze concurrently
    // Analyze is started early but its result is consumed later in the modal flow
    const analyzePromise = shouldAnalyzeActivity ?
    supabase.functions.invoke("analyze-activity", {
      body: {
        activity: data.activity,
        generatorSlug: config.slug,
        detectedCountry: data.country,
        detectedState: data.state
      }
    }).catch((err) => {
      console.error("Activity analysis failed (parallel):", err);
      return { data: null, error: err };
    }) :
    null;

    try {
      const { data: validationResult, error: validationError } = await edgeFunctions.validate({
        activity: data.activity,
        attachmentCounts,
        country: data.country,
        stateProvince: data.state,
      });

      // Check if cancelled during validation
      if (abortControllerRef.current?.signal.aborted) {
        updateGenerationLog(generationLogIdRef.current, { status: 'failed', error_message: 'User cancelled', error_stage: 'validating' });
        return;
      }
      if (validationError) {
        console.error("Validation error:", validationError);
      } else if (!validationResult?.valid) {
        setIsValidating(false);
        setShowExamplePrompts(true);
        const isSecurityBlock = validationResult?.error_code === 'unsafe_input';
        updateGenerationLog(generationLogIdRef.current, {
          status: 'failed',
          error_message: validationResult?.reason || 'Invalid prompt',
          error_stage: isSecurityBlock ? 'security_validation' : 'validating',
          completed_at: new Date().toISOString(),
          duration_ms: Date.now() - generationStartTimeRef.current
        });
        toast({
          title: isSecurityBlock ? "Content Not Allowed" : "Invalid Request",
          description: isSecurityBlock
            ? (validationResult?.reason || "Your description contains content we don't allow for safety reasons. Please rephrase.")
            : (validationResult?.reason || `Please describe a work activity suitable for a ${config.acronym} document.`) +
              " Alternatively, you can try clicking an example prompt below.",
          variant: "destructive"
        });
        return;
      }
    } catch (err) {
      console.error("Validation check failed:", err);
    }

    // Check if cancelled after validation
    if (abortControllerRef.current?.signal.aborted) return;
    setIsValidating(false);
    setIsModalInitializing(true);
    setModalState("processing");
    setGeneratedDocument(null);
    initializeProcessingSteps(attachmentCounts);

    // Minimum skeleton display time for smooth UX transition
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Check if cancelled during skeleton display
    if (abortControllerRef.current?.signal.aborted) return;
    setIsModalInitializing(false);

    // Track generate button click
    trackButtonClicked(EventContext.GENERATE);

    const submittedActivity = data.activity.trim();

    // Reverse lookup: convert names back to codes for state preservation on "Generate Another"
    const countryCode = COUNTRIES.find((c) => c.name === data.country)?.code || data.country;
    const countryData = COUNTRIES.find((c) => c.name === data.country);
    const stateCode = countryData?.states?.find((s) => s.name === data.state)?.code || data.state;
    setLastSubmittedCountry(countryCode);
    setLastSubmittedState(stateCode);
    setLastSubmittedActivity(submittedActivity);
      let searchResult: StandardsSearchResult | null = null;
      let searchError: Error | null = null;
      let enrichedCategories: string[] | undefined;
      let displayCategories: string[] | undefined;
      let expandedActivity: string | undefined;
      let resolvedDomain: string | undefined = config.analysis_domain;
      try {
        // Step 1: Complete validation step
        const validatingStart = Date.now();
        await waitMinimum(validatingStart);
        if (abortControllerRef.current?.signal.aborted) return;
        updateStepStatus("validating", "completed");

        // Step 2 & 3: Analyse activity and detect categories
        // Analysis was already fired concurrently with validation (Phase 2 optimization)
        if (shouldAnalyzeActivity) {
          const analyzingStart = Date.now();
          updateStepStatus("analyzing", "active");
          updateGenerationLog(generationLogIdRef.current, { status: 'analyzing' });
          try {
            // Await the pre-fired analysis promise
            const { data: analysisData, error: analysisError } = await analyzePromise!;
            if (abortControllerRef.current?.signal.aborted) return;
            if (!analysisError && analysisData) {
              // Log analysis result to Bugsnag in production, console in dev
              const analysisLogData = {
                expandedActivity: analysisData.expandedActivity,
                detectedCategories: analysisData.detectedCategories,
                categoryDisplayNames: analysisData.categoryDisplayNames,
                domain: analysisData.analysisMetadata?.domain,
                domainSpecificData: analysisData.domainSpecificData
              };

              console.log("Activity analysis result:", analysisLogData);

              if (analysisData.detectedCategories?.length > 0) {
                enrichedCategories = analysisData.detectedCategories;
              }
              if (analysisData.categoryDisplayNames?.length > 0) {
                displayCategories = analysisData.categoryDisplayNames;
              } else if (analysisData.detectedCategories?.length > 0) {
                displayCategories = analysisData.detectedCategories;
              }
              if (analysisData.expandedActivity) {
                expandedActivity = analysisData.expandedActivity;
              }

              const analysedDomain = analysisData.analysisMetadata?.domain;
              if (analysedDomain) {
                resolvedDomain = analysedDomain;
              }

              // Dynamic domain switching for multi-domain generators only
              // Skip override if generator has custom ANALYSIS-SPECIFIC ui_labels in the database
              // that differ from defaults (form-level labels and default-inherited keys don't block switching)
              const ANALYSIS_UI_LABEL_KEYS: (keyof GeneratorUILabels)[] = ['analysis_heading', 'scanning_label', 'scanning_message', 'identified_label', 'none_detected_message', 'step_detecting', 'step_processing'];
              const mergedLabels = generatorConfig?.ui_labels as Record<string, unknown> | undefined;
              const hasCustomAnalysisLabels = mergedLabels && ANALYSIS_UI_LABEL_KEYS.some(key =>
                key in mergedLabels && mergedLabels[key] !== DEFAULT_UI_LABELS[key]
              );
              if (analysedDomain && DOMAIN_UI_LABELS[analysedDomain] && !hasCustomAnalysisLabels) {
                setDetectedDomain(analysedDomain);
                const domainLabels = DOMAIN_UI_LABELS[analysedDomain] as GeneratorUILabels;
                setDynamicUiLabels(domainLabels);
                
                // Update processing step labels to match detected domain
                setProcessingSteps((prev) =>
                  prev.map((step) => {
                    if (step.id === "detecting" && domainLabels.step_detecting) {
                      return { ...step, label: domainLabels.step_detecting };
                    }
                    if (step.id === "hazards" && domainLabels.step_processing) {
                      return { ...step, label: domainLabels.step_processing };
                    }
                    return step;
                  })
                );
              }

              // Log jurisdiction discrepancy for safety domain (but don't auto-override)
              const jurisdictionClues = analysisData.domainSpecificData?.jurisdictionClues;
              if (jurisdictionClues?.inferredCountry || jurisdictionClues?.inferredState) {
                const hasDiscrepancy =
                jurisdictionClues.inferredCountry && jurisdictionClues.inferredCountry !== data.country ||
                jurisdictionClues.inferredState && jurisdictionClues.inferredState !== data.state;
                if (hasDiscrepancy) {
                  const discrepancyData = {
                    generatorSlug: generatorConfig?.slug,
                    activity: data.activity,
                    userSelected: { country: data.country, state: data.state },
                    inferredFromPrompt: { country: jurisdictionClues.inferredCountry, state: jurisdictionClues.inferredState },
                    reasoning: jurisdictionClues.reasoning,
                    detectedDomain: analysisData.analysisMetadata?.domain,
                    detectedCategories: analysisData.detectedCategories,
                    expandedActivity: analysisData.expandedActivity,
                  };

                  if (import.meta.env.DEV) {
                    console.log("Jurisdiction discrepancy detected:", discrepancyData);
                  } else {
                    Bugsnag.notify(new Error("Jurisdiction discrepancy detected"), (event) => {event.severity = "info";event.context = `analyze-activity/${generatorConfig?.slug}`;event.addMetadata("discrepancy", discrepancyData);event.addMetadata("request", { url: `analyze-activity`, country: data.country, state: data.state });});
                  }
                }
              }

              // Log if fallback was used
              if (analysisData.fallback) {
                if (import.meta.env.DEV) {
                  console.warn("Activity analysis used fallback due to error:", analysisData.error);
                } else {
                  Bugsnag.notify(new Error("Activity analysis fallback used"), (event) => {event.severity = "warning";event.context = "analyze-activity";event.addMetadata("fallback", { error: analysisData.error });});
                }
              }
            } else if (analysisError) {
              if (import.meta.env.DEV) {
                console.error("Activity analysis error:", analysisError);
              } else {
                Bugsnag.notify(analysisError, (event) => {event.context = "analyze-activity";});
              }
            }
          } catch (analysisErr) {
            // Continue with standard flow if analysis fails
            console.error("Activity analysis failed, continuing with standard flow:", analysisErr);
          }
          await waitMinimum(analyzingStart);
          if (abortControllerRef.current?.signal.aborted) return;
          updateStepStatus("analyzing", "completed");

          // Step 3: Detecting categories (using analysis output for display and enriched slugs for standards search)
          const detectingStart = Date.now();
          updateStepStatus("detecting", "active");
          const { data: searchData, error: searchErr } = await supabase.functions.invoke<StandardsSearchResult>(
            "search-standards",
            {
              body: {
                activity: data.activity,
                country: data.country,
                stateProvince: data.state,
                enrichedCategories,
                expandedActivity,
                domain: resolvedDomain,
                generatorSlug: config.slug,
                fallbackToInternational: config.standards_behavior?.fallback_to_international,
              }
            }
          );
          searchError = searchErr;
          if (abortControllerRef.current?.signal.aborted) return;
          searchResult = searchData;

          if (displayCategories?.length) {
            setDetectedCategories(displayCategories);
            await new Promise((resolve) => setTimeout(resolve, 250));
            setShowCategories(true);

            const categoryCount = displayCategories.length;
            const lastBadgeDelay = 300 + (categoryCount - 1) * 150;
            const bufferTime = 300;
            const dynamicDelay = Math.max(500, lastBadgeDelay + bufferTime);
            await new Promise((resolve) => setTimeout(resolve, dynamicDelay));
          }
          await waitMinimum(detectingStart);
          if (abortControllerRef.current?.signal.aborted) return;
          updateStepStatus("detecting", "completed");
        }

      // Step 4: Search for standards (conditional based on generator config)
      if (shouldSearchStandards) {
        updateStepStatus("searching", "active");
        setIsSearching(true);
        if (searchError) {
          console.error("Standards search error:", searchError);
          setIsSearching(false);
          setIsSearchComplete(true);
          updateStepStatus("searching", "completed");
        } else if (searchResult && searchResult.searchedSources.length > 0) {
          setMatchedStandards(searchResult.matchedStandards || []);
          // Start with all sources in "searching" state
          let currentSources: CarouselSubItem[] = searchResult.searchedSources.map((src) => ({
            id: src.name,
            label: src.name,
            status: "searching" as const,
            count: undefined
          }));
          setCarouselSubItems(currentSources);
          setTotalSourcesCount(searchResult.searchedSources.length);

          // Wait for UI to render
          await new Promise((resolve) => setTimeout(resolve, 300));
          if (abortControllerRef.current?.signal.aborted) return;

          // Process each source one by one
          for (let i = 0; i < searchResult.searchedSources.length; i++) {
            if (abortControllerRef.current?.signal.aborted) return;
            const source = searchResult.searchedSources[i];
            if (source.status === "found") {
              currentSources = currentSources.map((s) =>
              s.id === source.name ?
              {
                ...s,
                status: "found" as const,
                count: source.count
              } :
              s
              );
              setCarouselSubItems([...currentSources]);
              await new Promise((resolve) => setTimeout(resolve, 250));
            } else {
              currentSources = currentSources.map((s) =>
              s.id === source.name ?
              {
                ...s,
                status: "removing" as const
              } :
              s
              );
              setCarouselSubItems([...currentSources]);
              await new Promise((resolve) => setTimeout(resolve, 200));
              currentSources = currentSources.filter((s) => s.id !== source.name);
              setCarouselSubItems([...currentSources]);
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
          setIsSearching(false);
          setIsSearchComplete(true);
          updateStepStatus("searching", "completed");
        } else {
          setIsSearching(false);
          setIsSearchComplete(true);
          updateStepStatus("searching", "completed");
        }
      } else {
        // Skip standards search for generators that don't require it
        setIsSearchComplete(true);
      }

      // ============================================================
      // Stage 4: Per-file attachment analysis (kinds parallel, files within kind serial)
      // ============================================================
      type AttachmentResult = { tags?: string[]; summary?: string; error?: string };
      const attachmentResults: Record<"photo" | "plan" | "doc", AttachmentResult[]> = { photo: [], plan: [], doc: [] };

      const initBlocks: AttachmentKindBlock[] = [
        { kind: "photo", files: photos.map((_, i) => ({ id: `photo-${i}`, fileName: photoNames[i] || `photo-${i + 1}`, status: "pending" as const })) },
        { kind: "plan", files: plans.map((_, i) => ({ id: `plan-${i}`, fileName: planNames[i] || `drawing-${i + 1}`, status: "pending" as const })) },
        { kind: "doc", files: docs.map((_, i) => ({ id: `doc-${i}`, fileName: docNames[i] || `document-${i + 1}`, status: "pending" as const })) },
      ];
      setAttachmentBlocks(initBlocks);

      const updateFileRow = (kind: "photo" | "plan" | "doc", index: number, patch: Partial<AttachmentFileRow>) => {
        setAttachmentBlocks((prev) => prev.map((b) => b.kind !== kind ? b : {
          ...b,
          files: b.files.map((f, i) => i === index ? { ...f, ...patch } : f),
        }));
      };

      const runKindQueue = async (kind: "photo" | "plan" | "doc", files: string[], names: string[], stepId: string) => {
        if (files.length === 0) return;
        updateStepStatus(stepId, "active");
        for (let i = 0; i < files.length; i++) {
          if (abortControllerRef.current?.signal.aborted) return;
          updateFileRow(kind, i, { status: "analyzing" });
          try {
            const { data: res, error: err } = await edgeFunctions.analyzeAttachment({
              kind,
              file: { name: names[i], data: files[i] },
              runId,
              fileIndex: i,
            });
            if (err || res?.error) {
              const message = err?.message || res?.error || "Analysis failed";
              attachmentResults[kind].push({ error: message });
              updateFileRow(kind, i, { status: "error", errorMessage: message });
            } else {
              attachmentResults[kind].push({ tags: res?.tags, summary: res?.summary });
              updateFileRow(kind, i, { status: "completed", tagCount: Array.isArray(res?.tags) ? res.tags.length : 0 });
            }
          } catch (e) {
            const message = (e as Error).message || "Analysis failed";
            attachmentResults[kind].push({ error: message });
            updateFileRow(kind, i, { status: "error", errorMessage: message });
          }
        }
        updateStepStatus(stepId, "completed");
      };

      if (hasAnyAttachment) {
        await Promise.all([
          runKindQueue("photo", photos, photoNames, "analyzing-photos"),
          runKindQueue("plan", plans, planNames, "analyzing-drawings"),
          runKindQueue("doc", docs, docNames, "analyzing-docs"),
        ]);
        if (abortControllerRef.current?.signal.aborted) return;
      }

      // ============================================================
      // Stage 4: Correlate insights across hazards + attachments
      // ============================================================
      let correlationContext: { evidenceLinks: number; newHazards: number; contradictions: number; raw?: unknown } | undefined;
      if (hasAnyAttachment) {
        updateStepStatus("cross-referencing", "active");
        setCorrelationStatus({ status: "running" });
        try {
          const { data: corr, error: corrErr } = await edgeFunctions.correlateInsights(runId);
          if (corrErr || corr?.error) {
            console.warn("Correlation failed:", corrErr || corr?.error);
            setCorrelationStatus({ status: "error" });
            updateStepStatus("cross-referencing", "error");
          } else {
            const evidenceLinks = Array.isArray(corr?.correlations) ? corr.correlations.length : 0;
            const newHazards = Array.isArray(corr?.newHazards) ? corr.newHazards.length : 0;
            const contradictions = Array.isArray(corr?.contradictions) ? corr.contradictions.length : 0;
            correlationContext = { evidenceLinks, newHazards, contradictions, raw: corr };
            // Build hazard → source-file provenance from correlate-insights output (all generators).
            let hazardProvenance: HazardProvenanceItem[] | undefined;
            if (Array.isArray(corr?.newHazards) && corr.newHazards.length > 0) {
              // insight_id format: r_{shortRun}:{kindToken}:{fileIndex}:0  (kindToken: photo|drawing|doc)
              const kindTokenToBlock: Record<string, "photo" | "plan" | "doc"> = { photo: "photo", drawing: "plan", doc: "doc" };
              const nameLookup = (insightId: string): { kind: "photo" | "plan" | "doc"; fileName: string } | null => {
                const parts = insightId.split(":");
                if (parts.length < 3) return null;
                const kindToken = parts[1];
                const fileIndex = parseInt(parts[2], 10);
                const blockKind = kindTokenToBlock[kindToken];
                if (!blockKind || Number.isNaN(fileIndex)) return null;
                const block = initBlocks.find((b) => b.kind === blockKind);
                const file = block?.files[fileIndex];
                if (!file) return null;
                return { kind: blockKind, fileName: file.fileName };
              };
              hazardProvenance = (corr.newHazards as Array<{ label: string; detail?: string; source_insight_ids?: string[] }>).map((h) => {
                const sources = (h.source_insight_ids ?? [])
                  .map(nameLookup)
                  .filter((s): s is { kind: "photo" | "plan" | "doc"; fileName: string } => s !== null);
                // De-duplicate by kind+fileName
                const seen = new Set<string>();
                const unique = sources.filter((s) => {
                  const k = `${s.kind}::${s.fileName}`;
                  if (seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
                return { label: h.label, detail: h.detail, sources: unique };
              });
            }
            setCorrelationStatus({ status: "completed", evidenceLinks, newHazards, contradictions, hazardProvenance });
            updateStepStatus("cross-referencing", "completed");
          }
        } catch (e) {
          console.warn("Correlation threw:", e);
          setCorrelationStatus({ status: "error" });
          updateStepStatus("cross-referencing", "error");
        }
      }

      // Build attachment context summary for generate-document.
      // See src/lib/attachment-context.ts for the citation contract.
      const shortRun = shortRunFromRunId(runId);

      const attachmentContext = hasAnyAttachment ? {
        photos: formatAttachmentKind({ results: attachmentResults.photo, names: photoNames, kindToken: "photo", labelPrefix: "PHOTO", fileIndexBase: 0, shortRun }),
        drawings: formatAttachmentKind({ results: attachmentResults.plan, names: planNames, kindToken: "drawing", labelPrefix: "DRAWING", fileIndexBase: 0, shortRun }),
        docs: formatAttachmentKind({ results: attachmentResults.doc, names: docNames, kindToken: "doc", labelPrefix: "DOC", fileIndexBase: 0, shortRun }),
      } : undefined;

      // Format correlation output into the text blocks generate-document expects.
      const formattedCorrelation = formatCorrelation(
        correlationContext?.raw as Parameters<typeof formatCorrelation>[0],
      );

      // Brief pause before generation phase
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (abortControllerRef.current?.signal.aborted) return;

      // Step 4: Building document structure
      const structuringStart = Date.now();
      updateStepStatus("structuring", "active");
      updateGenerationLog(generationLogIdRef.current, { status: 'generating' });
      // Generate document via the generic pipeline
      const { data: result, error } = await edgeFunctions.generate({
        activity: data.activity,
        country: data.country,
        stateProvince: data.state,
        model: data.model,
        photos: photos,
        plans: plans,
        docs: docs,
        runId,
        attachmentContext,
        correlationContext: formattedCorrelation,
        standardsContext: searchResult?.standardsContext,
        matchedStandards: searchResult?.matchedStandards,
        detectedCategories: enrichedCategories ?? searchResult?.categories,
        detectedDomain: resolvedDomain || undefined
      });
      if (abortControllerRef.current?.signal.aborted) return;
      if (error) {
        throw new Error(error.message || "Failed to generate document");
      }
      // Also check if error is in the data object (Supabase may return errors there on 500 responses)
      if (result?.error) {
        throw new Error(result.error || "Document generation failed");
      }
      if (!result?.document) {
        throw new Error("No document returned from generation");
      }
      // Narrow the modal's standards list to only those the doc actually
      // cites after server-side enforcement. Guarantees modal == doc.
      if (Array.isArray(result.appliedStandards) && searchResult?.matchedStandards) {
        const appliedLower = new Set(
          (result.appliedStandards as string[]).map((s) => (s || "").toLowerCase()),
        );
        const filtered = searchResult.matchedStandards.filter((m) =>
          appliedLower.has((m.source_document || "").toLowerCase()),
        );
        setMatchedStandards(filtered);
      }
      await waitMinimum(structuringStart);
      if (abortControllerRef.current?.signal.aborted) return;
      updateStepStatus("structuring", "completed");

      // Step 5: Processing hazards and controls
      const hazardsStart = Date.now();
      updateStepStatus("hazards", "active");
      await waitMinimum(hazardsStart);
      if (abortControllerRef.current?.signal.aborted) return;
      updateStepStatus("hazards", "completed");

      // Step 6: Completing document
      const completingStart = Date.now();
      updateStepStatus("completing", "active");
      await waitMinimum(completingStart);
      if (abortControllerRef.current?.signal.aborted) return;
      const document: SwmsDocument = {
        ...result.document,
        generatorType: generatorSlug // Use dynamic generator slug instead of hardcoded 'swms'
      };
      updateStepStatus("completing", "completed");
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (abortControllerRef.current?.signal.aborted) return;
      setGeneratedDocument(document);
      setModalState("ready");
      logFunnelEvent('document_generated', { export_format: data.exportFormat });

      // Calculate generation duration (safe: ref initialized to 0, only calculate if > 0)
      const durationSeconds =
      generationStartTimeRef.current > 0 ?
      Math.round((Date.now() - generationStartTimeRef.current) / 1000) :
      undefined;

      // Track email capture form modal with generation metadata
      trackEmailCaptureFormModal({
        model: lastUsedModelRef.current || undefined,
        durationSeconds,
        swmsType: getTrackedDocumentType(document, submittedActivity)
      });

      // Log AI generation complete (final 'completed' set after file download)
      updateGenerationLog(generationLogIdRef.current, {
        status: 'generated',
      });
    } catch (error) {
      console.error("Generation failed:", error);

      // Determine which stage failed based on current active step.
      // Fall back to 'generating' (not 'unknown') because by the time this
      // catch fires the generate-document invoke is the dominant failure
      // surface. Map any non-standard step id to a documented stage so we
      // never write 'unknown' to generation_logs.error_stage.
      const allowedStages = new Set([
        'validating',
        'security_validation',
        'generating',
        'exporting',
      ]);
      const activeStep = processingSteps.find((s) => s.status === 'active');
      const rawStage = activeStep?.id;
      const errorStage = rawStage && allowedStages.has(rawStage) ? rawStage : 'generating';

      // Log generation failure
      updateGenerationLog(generationLogIdRef.current, {
        status: 'failed',
        error_message: error instanceof Error ? error.message.substring(0, 500) : 'Unknown error',
        error_stage: errorStage,
        completed_at: new Date().toISOString(),
        duration_ms: Date.now() - generationStartTimeRef.current
      });

      // Mark current active step as error
      setProcessingSteps((prev) =>
      prev.map((step) =>
      step.status === "active" ?
      {
        ...step,
        status: "error"
      } :
      step
      )
      );

      // Check if this is a rate limit error
      const rateLimitCheck = isRateLimitError(error);
      if (rateLimitCheck.isRateLimit) {
        toast({
          title: "Rate Limit Reached",
          description: rateLimitCheck.message || "You've made too many requests. Please try again later.",
          variant: "destructive"
        });
        setModalState("closed");
        return;
      }

      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      setModalState("closed");
    }
  };
  const handleDownload = async () => {
    if (!generatedDocument || !email || !phone) return;
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    if (!trimmedFirstName || !trimmedLastName) return;

    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      toast({
        title: "Invalid email",
        description: emailValidation.error,
        variant: "destructive"
      });
      return;
    }

    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.valid) {
      toast({
        title: "Invalid phone number",
        description: phoneValidation.error,
        variant: "destructive"
      });
      return;
    }

    const trackedDocumentType = getTrackedDocumentType(generatedDocument, lastSubmittedActivity);

    // Track download button click and create lead
    trackButtonClicked(EventContext.DOWNLOAD);
    trackLeadCreated({
      email,
      phone,
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      country: lastSubmittedCountry,
      swmsType: trackedDocumentType,
      rawType: generatedDocument.TYPE,
      generatedActivity: generatedDocument.ACTIVITY,
      submittedActivity: lastSubmittedActivity,
    });
    logFunnelEvent('lead_captured', { country: lastSubmittedCountry });
    setIsExporting(true);
    updateGenerationLog(generationLogIdRef.current, { status: 'exporting' });
    try {
      // Choose export function based on selected format
      const exportFunction = selectedExportFormat === "xlsx" ? "export-xlsx" : "export-docx";
      const mimeType = selectedExportFormat === "xlsx" ?
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      const { data, error } = await supabase.functions.invoke(exportFunction, {
        body: {
          document: generatedDocument,
          email,
          generatorSlug: config.slug // CRITICAL: Pass generator slug for correct template selection
        }
      });
      if (error) throw error;
      const byteCharacters = atob(data.file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setModalState("success");

      // Track file download and download complete modal
      trackFileSuccessfullyDownloaded(trackedDocumentType);
      trackDownloadCompleteModal();
      logFunnelEvent('file_downloaded', { export_format: selectedExportFormat });

      // Mark generation as fully completed after successful download
      updateGenerationLog(generationLogIdRef.current, {
        status: 'completed',
        completed_at: new Date().toISOString(),
        duration_ms: Date.now() - generationStartTimeRef.current
      });

      // Update feedback data with SWMS summary and email
      setFeedbackData((prev) => ({
        ...prev,
        swmsSummary: generatedDocument.ACTIVITY || "",
        email: email
      }));
    } catch (error) {
      console.error("Export error:", error);
      updateGenerationLog(generationLogIdRef.current, {
        status: 'failed',
        error_message: error instanceof Error ? error.message : 'Export failed',
        error_stage: 'exporting',
        completed_at: new Date().toISOString(),
        duration_ms: Date.now() - generationStartTimeRef.current
      });
      toast({
        title: "Export Failed",
        description: "There was an error exporting your document. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };
  const handleReset = () => {
    // Abort any in-progress generation
    abortControllerRef.current?.abort();

    // Track "Generate Another SWMS" button click if coming from success state
    if (modalState === "success") {
      trackButtonClicked(EventContext.GENERATE_ANOTHER_SWMS);
    }
    // Submit any pending feedback before resetting
    submitFeedback();
    setGeneratedDocument(null);
    setModalState("closed");
    setProcessingSteps([]);
    setCarouselSubItems([]);
    setMatchedStandards([]);
    setDetectedCategories([]);
    setShowCategories(false);
    setIsSearching(false);
    setIsSearchComplete(false);
    setTotalSourcesCount(0);
    setIsModalInitializing(false);
    setEmail("");
    setPhone("");
    setFirstName("");
    setLastName("");
    setLastSubmittedCountry("");
    setLastSubmittedState("");
    setLastSubmittedActivity("");
    setFeedbackData({
      swmsSummary: "",
      email: "",
      starRating: 0,
      referralSource: "",
      comments: ""
    });
    setHoveredStar(0);
    setFeedbackSubmitted(false);
    hasTrackedFeedbackComment.current = false;
    setChatResetKey((prev) => prev + 1);
  };
  const handleFeedbackSubmit = () => {
    submitFeedback();
    trackButtonClicked(EventContext.FEEDBACK_SUBMITTED, {
      rating: feedbackData.starRating || undefined,
      referralSource: feedbackData.referralSource || undefined,
      comments: feedbackData.comments || undefined,
      swmsType: getTrackedDocumentType(generatedDocument)
    });
    toast({
      title: "Thank you!",
      description: "Your feedback has been submitted."
    });
  };
  const handleCopyLink = () => {
    // Submit any pending feedback
    submitFeedback();
    trackButtonClicked(EventContext.COPY_WEBSITE_LINK);
    const currentDomain = window.location.origin;
    navigator.clipboard.writeText(currentDomain);
    toast({
      title: "Link Copied",
      description: `${window.location.hostname} copied to clipboard.`
    });
  };
  const handleModalClose = (open: boolean) => {
    if (!open) {
      if (modalState === "processing" || modalState === "ready") {
        setShowExitConfirm(true);
      } else if (modalState === "success") {
        handleReset();
      }
    }
  };
  const handleConfirmExit = () => {
    // Log cancellation before aborting
    updateGenerationLog(generationLogIdRef.current, {
      status: 'failed',
      error_message: 'User cancelled',
      error_stage: 'user_cancelled',
      completed_at: new Date().toISOString(),
      duration_ms: generationStartTimeRef.current > 0 ? Date.now() - generationStartTimeRef.current : null
    });
    abortControllerRef.current?.abort();
    setShowExitConfirm(false);
    handleReset();
    toast({
      title: "Generation Cancelled",
      description: config.ui_labels?.cancel_toast_message ||
      `Your ${config.acronym || 'document'} generation has been stopped.`
    });
  };
  const handlePointerDownOutside = (e: Event) => {
    if (modalState === "processing") {
      e.preventDefault();
    } else if (modalState === "success") {
      submitFeedback();
    }
  };
  const updateFeedback = (field: keyof FeedbackData, value: string | number) => {
    setFeedbackData((prev) => ({
      ...prev,
      [field]: value
    }));
  };
  const isModalOpen = modalState !== "closed";

  // Show error page for unconfigured production domains
  if (!isDomainLoading && isUnconfiguredDomain) {
    return <DomainNotConfigured hostname={detectedHostname} />;
  }

  // Show loading state while detecting generator - prevents flash of wrong content
  if (isInitializing) {
    return (
      <div className="min-h-screen gradient-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>);

  }

  return (
    <div className={cn("min-h-screen relative", layoutName === "split-preview" ? "" : "gradient-background")} style={layoutName === "split-preview" ? { backgroundColor: '#FBFBFB' } : undefined}>
      {/* About modal button - positioned per variant */}
      <div className={cn(
        "absolute z-10",
        layoutName === "split-preview" ?
        "top-[calc(1.5rem+20px)] right-[calc(1.5rem+20px)]" :
        "top-6 right-6"
      )}>
        <AboutModal config={config} variant={layoutName === "split-preview" ? "white" : "default"} />
      </div>

      {/* Dev mode checklist for localhost/preview domains */}
      {isDevMode &&
      <DevModeChecklist
        currentSlug={generatorSlug}
        detectedHostname={detectedHostname}
        isOverridden={isOverridden}
        currentVariantKey={variantKey}
        availableVariants={availableVariants}
        onVariantChange={setVariantKey} />

      }
      
      {/* GeneratorSelector hidden for now */}

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent
          className={cn(
            modalState === "processing" && "max-w-3xl",
            modalState === "ready" && "max-w-md",
            modalState === "success" && (() => {
              const hasMarketingContent = Boolean(
                config.success_marketing_heading1 &&
                config.success_marketing_heading2 &&
                config.success_marketing_image &&
                config.success_cta_label &&
                config.success_cta_url
              );
              const showBanner = hasMarketingContent || isDevMode;
              return showBanner && !isBannerCollapsed ? "max-w-2xl" : "max-w-md";
            })()
          )}
          onPointerDownOutside={handlePointerDownOutside}>
          
          <div className="w-full min-w-0 overflow-hidden transition-all duration-300 ease-in-out">
            {modalState === "processing" && isModalInitializing && <ProcessingSkeleton />}

            {modalState === "processing" && !isModalInitializing &&
            <div className="flex w-full min-w-0 gap-8 overflow-hidden py-4 min-h-[440px] animate-fade-in">
                {/* Left Column: Carousels */}
                <div className="flex-1 min-w-0 overflow-hidden border-r border-border pr-6">
                  <ProcessingCarousels
                  detectedCategories={detectedCategories}
                  showCategories={showCategories}
                  subItems={carouselSubItems}
                  matchedStandards={matchedStandards}
                  isSearching={isSearching}
                  isSearchComplete={isSearchComplete}
                  jurisdiction={lastSubmittedCountry}
                  isAnalyzingHazards={processingSteps.find((s) => s.id === "detecting")?.status === "active"}
                  totalSourcesCount={totalSourcesCount}
                  uiLabels={dynamicUiLabels || config.ui_labels || DEFAULT_UI_LABELS}
                  showStandardsSection={shouldSearchStandards}
                  showAnalysisSection={shouldAnalyzeActivity}
                  attachmentSection={attachmentBlocks.some(b => b.files.length > 0) ? <AttachmentAnalysisRows blocks={attachmentBlocks} /> : null}
                  correlationSection={<CorrelationSummaryRow {...correlationStatus} />} />
                
                </div>
                {/* Right Column: Step List */}
                <div className="w-52 flex-shrink-0">
                  <ProcessingStepList steps={processingSteps} />
                </div>
              </div>
            }

            {modalState === "ready" &&
            <div className="space-y-6 py-4 animate-in fade-in duration-300">
                <div className="text-center space-y-3">
                  <div className="flex justify-center">
                    <CheckCircle2 className="h-12 w-12 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">Your {(config.full_name || config.display_name || 'Document').replace(/^.*Checklist.*$/i, 'Checklist').replace(/^.*Permit.*$/i, 'Permit').replace(/^.*Assessment.*$/i, 'Assessment')} is Ready</h3>
                  <p className="text-sm text-muted-foreground">Enter your details to download the document.</p>
                </div>

                <div className="space-y-3">
                  <Input
                      id="first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First Name"
                      disabled={isExporting} />
                    <Input
                      id="last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last Name"
                      disabled={isExporting} />
                  <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  disabled={isExporting} />


                  <PhoneInput
                  value={phone}
                  onChange={(value) => setPhone(value || "")}
                  defaultCountry={defaultPhoneCountry}
                  placeholder="Phone Number"
                  disabled={isExporting}
                  onKeyDown={(e) => e.key === "Enter" && email && phone && firstName.trim() && lastName.trim() && !isExporting && handleDownload()} />

                </div>

                <Button
                onClick={handleDownload}
                disabled={isExporting || !email || !phone || !firstName.trim() || !lastName.trim()}
                className="w-full"
                size="lg">
                
                  {isExporting ?
                <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Preparing Download...
                    </> :

                <>
                      <Download className="mr-2 h-5 w-5" />
                      Download {selectedExportFormat === "xlsx" ? "Excel" : "Word"} Document
                    </>
                }
                </Button>
              </div>
            }

            {modalState === "success" && (() => {
              const hasMarketingContent = Boolean(
                config.success_marketing_heading1 &&
                config.success_marketing_heading2 &&
                config.success_marketing_image &&
                config.success_cta_label &&
                config.success_cta_url
              );
              const showBanner = hasMarketingContent || isDevMode;

              return (
                <div className={cn(
                  "animate-in fade-in duration-300",
                  showBanner ? "flex flex-col sm:flex-row" : ""
                )}>
                  {showBanner &&
                  <SuccessMarketingBanner
                    heading1={config.success_marketing_heading1}
                    heading2={config.success_marketing_heading2}
                    imageUrl={config.success_marketing_image}
                    ctaLabel={config.success_cta_label}
                    ctaUrl={config.success_cta_url}
                    generatorSlug={config.slug}
                    isDevMode={isDevMode}
                    onCollapseChange={setIsBannerCollapsed} />

                  }
                  <div className="flex-1 flex flex-col bg-gray-50 rounded-r-lg p-5 w-1/2 shrink-0">
                     {/* Centered content group */}
                     <div className="flex-1 flex flex-col items-center justify-center gap-4">
                     <h3 className="text-lg font-semibold text-foreground text-center">
                          {formatDomainForDisplay(config.primary_domain, config.display_name, config.acronym)}
                         </h3>

                     <div className="w-full space-y-4">
                      <div className="text-center space-y-3">
                        <div className="flex justify-center">
                          <CheckCircle2 className="h-12 w-12 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">Download Complete!</h3>
                        <p className="text-sm text-muted-foreground">Your {config.full_name || config.display_name || 'document'} has been downloaded successfully.</p>
                      </div>

                      {/* Feedback Section */}
                      {!feedbackSubmitted ?
                        <div className="space-y-4 border-t border-border pt-4">
                          {/* Star Rating */}
                          <div className="text-center space-y-2">
                            <p className="text-sm text-muted-foreground">How would you rate your experience?</p>
                            <div className="flex justify-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) =>
                              <button
                                key={star}
                                type="button"
                                onClick={() => {
                                  updateFeedback("starRating", star);
                                  trackUserSubmittedRating(star, getTrackedDocumentType(generatedDocument));
                                }}
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                                className="p-1 transition-transform hover:scale-110"
                                aria-label={`Rate ${star} stars`}>
                                
                                  <Star
                                  className={`h-8 w-8 transition-colors ${star <= (hoveredStar || feedbackData.starRating) ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                                
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Referral & Comments - shown after selecting stars */}
                          {feedbackData.starRating > 0 &&
                          <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                              <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">
                                  How did you hear about {config.display_name || 'this'} Generator?
                                </Label>
                                <div className="grid grid-cols-2 gap-2">
                                  {REFERRAL_OPTIONS.map((option) =>
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => {
                                    updateFeedback("referralSource", option.value);
                                    trackUserSubmittedReferralSource(option.label, getTrackedDocumentType(generatedDocument));
                                  }}
                                  className={`px-4 py-2 text-sm rounded-full border transition-colors ${feedbackData.referralSource === option.value ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:bg-primary hover:text-primary-foreground hover:border-primary"}`}>
                                  
                                      {option.label}
                                    </button>
                                )}
                                </div>
                              </div>

                              {/* Comments */}
                              <Textarea
                              value={feedbackData.comments}
                              onChange={(e) => {
                                updateFeedback("comments", e.target.value);
                                if (e.target.value.length > 0 && !hasTrackedFeedbackComment.current) {
                                  hasTrackedFeedbackComment.current = true;
                                  trackFeedbackCommented(getTrackedDocumentType(generatedDocument));
                                }
                              }}
                              placeholder="Any comments, ideas, or requests? (optional)"
                              className="min-h-[80px] resize-none" />
                            

                              <Button onClick={handleFeedbackSubmit} className="w-full">
                                Submit Feedback
                              </Button>
                            </div>
                          }
                        </div> :

                        <div className="text-center py-4 border-t border-border animate-in fade-in duration-200">
                          <p className="text-sm text-muted-foreground">Thank you for your feedback!</p>
                        </div>
                        }
                     </div>
                     </div>{/* end centered content group */}

                     {/* Bottom: Action buttons aligned with CTA on left */}
                     <div className="mt-auto pt-4 pb-1 space-y-3">
                      <Button variant="outline" className="w-full h-11" size="lg" onClick={handleCopyLink}>
                        <Link2 className="mr-2 h-5 w-5" />
                        Copy Website Link
                      </Button>

                      <Button onClick={handleReset} variant="outline" className="w-full h-11 whitespace-nowrap" size="lg">
                        <RefreshCw className="mr-2 h-5 w-5 shrink-0" />
                        Generate Another Free {(() => {
                          const DESCRIPTIVE_OVERRIDES: Record<string, string> = {
                            'ECL': 'Checklist',
                            'QAC': 'Checklist',
                            'SCL': 'Checklist',
                            'SM': 'SM Minutes'
                          };
                          return DESCRIPTIVE_OVERRIDES[config.acronym] ?? config.acronym;
                        })()}
                      </Button>
                    </div>
                  </div>
                </div>);

            })()}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to exit?</AlertDialogTitle>
            <AlertDialogDescription>This will cancel the {config.acronym || 'document'} generation.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmExit}>Exit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {layoutName === "split-preview" ? (
      /* Split-preview variant: white page with rounded right panel */
      <div className="min-h-screen flex flex-col lg:flex-row items-stretch p-0" style={{ backgroundColor: '#FBFBFB' }}>
          {/* Left column: heading + form */}
           <div className="lg:w-[48%] flex flex-col justify-center px-6 pt-24 pb-6 lg:pt-0 lg:pb-0 lg:pl-[8%] lg:pr-[4%] lg:py-0 text-center lg:text-left">
            <h1 className="text-foreground mb-6 text-3xl font-bold leading-tight tracking-tight text-center md:text-3xl">
              {variantConfig.hero_heading as string || config.hero_heading}
            </h1>

            <ChatInterface
            key={chatResetKey}
            placeholder={config.placeholder}
            onSubmit={(data) => {
              setShowExamplePrompts(false);
              handleGenerate(data);
            }}
            isProcessing={modalState === "processing"}
            isValidating={isValidating}
            showExamplePrompts={showExamplePrompts}
            examplePrompts={config.example_prompts}
            supportedExportFormats={config.supported_export_formats}
            supportedCountries={config.supported_countries}
            initialCountry={lastSubmittedCountry}
            initialState={lastSubmittedState}
            attachmentConfig={config.attachment_config}
            generatorSlug={config.slug}
            variantKey={variantKey}
            compact
            generatorDisplayName={config.display_name}
            bannerDescriptor={config.placeholder}
            maxPromptLength={config.max_prompt_length} />
          

            <p className="mt-2 text-sm text-muted-foreground text-center">
              {config.disclaimer}
            </p>

            <div className="mt-6 mb-2">
              <SitemateFooter fixed={false} generatorSlug={generatorSlug} placement="footer" />
            </div>
          </div>

          {/* Right column: rounded card panel with animated fluid bg */}
           <div className="lg:w-[52%] flex flex-col items-center justify-center px-4 py-8 lg:p-3">
             <div className="w-full h-full rounded-3xl flex flex-col items-center justify-center px-4 py-8 lg:px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #edf4fc 40%, #d8e8f8 100%)' }}>
              {/* Animated fluid blobs — more visible and spread out */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute w-[75%] h-[75%] rounded-full opacity-25 blur-2xl animate-fluid-blob-1" style={{ background: 'radial-gradient(circle, #6AADE8 0%, transparent 70%)', top: '-15%', left: '-10%' }} />
                <div className="absolute w-[65%] h-[65%] rounded-full opacity-30 blur-2xl animate-fluid-blob-2" style={{ background: 'radial-gradient(circle, #7DB8EC 0%, transparent 70%)', top: '55%', right: '-15%' }} />
                <div className="absolute w-[70%] h-[70%] rounded-full opacity-20 blur-2xl animate-fluid-blob-3" style={{ background: 'radial-gradient(circle, #6AADE8 0%, transparent 70%)', bottom: '-20%', left: '40%' }} />
                <div className="absolute w-[55%] h-[55%] rounded-full opacity-35 blur-2xl animate-fluid-blob-4" style={{ background: 'radial-gradient(circle, #8DC3F0 0%, transparent 70%)', top: '25%', right: '50%' }} />
              </div>
              <div className="relative z-10 flex flex-col items-center justify-center w-full">
              {variantConfig.hero_image as string &&
              <div className="relative p-1.5 rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
              <img
                  src={variantConfig.hero_image as string}
                  alt={`${config.display_name} document preview`}
                  className={cn(
                     "rounded-lg object-contain w-full",
                     variantConfig.image_orientation as string === "portrait" ?
                     "max-w-[260px] lg:max-w-[340px]" :
                     "max-w-[440px] lg:max-w-[520px]"
                   )}
                  style={{ boxShadow: '0 10px 25px -5px rgba(46, 129, 218, 0.15), 0 4px 10px -5px rgba(46, 129, 218, 0.1)' }} />
              </div>

              }
              <p className="mt-6 text-center text-sm max-w-md text-muted-foreground">
                {config.hero_subheading}
              </p>
              </div>
            </div>

          </div>
        </div>) : (

      /* Default variant: centered single-column layout */
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-3xl text-center mb-8">
            <h1 className="text-foreground mb-4 md:text-6xl text-4xl font-bold">{config.hero_heading}</h1>
            <p className="text-lg text-[#8299ae]">{config.hero_subheading}</p>
          </div>

          <ChatInterface
          key={chatResetKey}
          placeholder={config.placeholder}
          onSubmit={(data) => {
            setShowExamplePrompts(false);
            handleGenerate(data);
          }}
          isProcessing={modalState === "processing"}
          isValidating={isValidating}
          showExamplePrompts={showExamplePrompts}
          examplePrompts={config.example_prompts}
          supportedExportFormats={config.supported_export_formats}
          supportedCountries={config.supported_countries}
          initialCountry={lastSubmittedCountry}
          initialState={lastSubmittedState}
          attachmentConfig={config.attachment_config}
          generatorSlug={config.slug}
          variantKey={variantKey}
          maxPromptLength={config.max_prompt_length} />
        

          <p className="mt-3 text-center text-sm text-[#8299ae]">
            {config.disclaimer}
          </p>

          <SitemateFooter generatorSlug={generatorSlug} placement="footer" />
        </div>)
      }
    </div>);

};
export default Index;